import styled from 'styled-components';

import { PickActionType } from '@/core/components/PickActionType';
import { BackgroundElement } from '@/core/interfaces/common';

import { hexToRgb } from '@/utils/helpers';
import mediaQueries from '@/utils/mediaQueries';
import { colors } from '@/utils/constants';

import {
  HeaderElement,
  TextDescription,
  WhyTakeActionWrapper,
} from '@/themes/globalStyles';

const {
  desktop,
  small,
  tablet,
  xxxsmall,
} = mediaQueries;

const maxDesktopWidth = '1250px';
const twoColumnLayout = '(min-width: 1024px)';

export const LandingPageWrapper = styled.div<BackgroundElement & { isActionTaken?: boolean }>`
  width: 100%;
  height: 100vh;
  background-color: ${(props): string => {
    if (props.isActionTaken) {
      return props.backgroundcolor || colors.white;
    }

    return colors.white;
  }};
  font-family: Roboto, sans-serif;
  // background-color: ${props => hexToRgb(props.backgroundcolor || colors.white, '1.00')};
  // background: repeating-linear-gradient(
  //   45deg,
  //   ${props => hexToRgb(props.backgroundcolor || colors.white, '0.20')},
  //   ${props => hexToRgb(props.backgroundcolor || colors.white, '0.20')} 10px,
  //   ${props => hexToRgb(props.backgroundcolor || colors.white, '0.40')} 10px,
  //   ${props => hexToRgb(props.backgroundcolor || colors.white, '0.40')} 20px,
  //   #465298 20px
  // );
`;

export const StyledWhyTakeActionWrapper = styled(WhyTakeActionWrapper)`
  max-width: unset;
  margin-bottom: 24px;
  padding-top: 24px;
`;

export const StyledTextDescription = styled(TextDescription)`
  margin-top: 10px;

  // The body is real HTML whose block elements control layout, so collapse the
  // raw newlines between paragraphs and list items instead of preserving them
  // as visible whitespace. The base TextDescription keeps pre-wrap for the
  // plain-text path.
  white-space: normal;

  // Now that the raw newlines are collapsed, restore spacing between blocks via
  // real margins so paragraphs, lists, and headers are visually separated.
  p,
  ul,
  ol,
  h1, h2, h3,
  blockquote {
    margin: 0 0 16px;
  }

  // No trailing gap after the final block.
  > *:last-child {
    margin-bottom: 0;
  }

  li {
    line-height: 32px;  // Same as line height for second description paragraph
  }

  h1, h2, h3, h4, h5, h6 {
    font-weight: bold;
  }
  // There is already an h1 element added above the description, so
  // customers *really* shouldn't add their own h1. A little extra
  // top spacing sets them off from the preceding block.
  h1 {
    font-size: 28px;
    margin-top: 24px;
    font-weight: bold;
  }
  h2 {
    font-size: 26px;
    margin-top: 22px;
    font-weight: bold;
  }
  h3 {
    font-size: 22px;
    margin-top: 20px;
    font-weight: bold;
  }
  h4, h5, h6 {
    font-size: 18px;
    margin-top: 18px;
    font-weight: bold;
  }

  blockquote {
    padding-left: 1rem;
    border-left: 3px solid currentColor;
  }
`;

export const StyledPickActionType = styled(PickActionType)`
  margin: 0;
`;

export const PickActionTypeWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

export const ResetFormButton = styled.button`
  display: block;
  border: 0;
  background-color: inherit;
  cursor: pointer;
`;

export const ResetForm = styled.span<BackgroundElement>`
  color: ${props => (props.textcolor)};
  font-weight: 700;
`;

export const Title = styled.h1`
  margin-top: 1rem;
  font-weight: 700;
  font-size: 24px;
  //text-align: center;
  text-align: left;
  padding: 0 12px 12px;

  @media ${desktop} {
    font-size: 32px;
    padding: 0;
  }
`;

// Section column for the form
export const FormSection = styled.section<BackgroundElement>`
  margin-top: 24px;
  padding: 20px 40px;
  border-width: 3px;
  border-style: solid;
  border-radius: 10px;
  border-color: ${props => hexToRgb(props.backgroundcolor || colors.white, '0.15')};
  background-color: ${props => hexToRgb(props.backgroundcolor || colors.white, '0.05')};

  @media ${xxxsmall} {
    padding: 16px 12px;
  }

  @media ${desktop} {
    //width: 600px;
  }
`;

export const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  background-color: ${colors.white};
  //border-radius: 40px;
  max-width: ${maxDesktopWidth};
  margin-left: auto;
  margin-right: auto;

  // Smaller both for guidance purposes and total message readability
  textarea.muster-personal-message {
    min-height: 100px;
  }

  textarea:not(.muster-personal-message) {
    min-height: 240px;
  }

  @media ${xxxsmall} {
    padding: 12px;
  }

  @media ${small} {
    padding: 4px;
    margin-bottom: 40px;
  }

  @media ${twoColumnLayout} {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 52px;
  }
`;

export const Header = styled.header<HeaderElement>`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-height: ${props => (props.isActionTaken ? '145px' : 'unset')};
  padding: 0px 16px 0px;
  background-color: ${props => props.backgroundcolor};
  color: ${props => props.textcolor};

  @media ${small} {
    align-items: flex-start;
  }

  @media ${tablet} {
    padding: 0 10px 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  @media ${desktop} {
    padding: 0 10px 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  // &::after {
  //   content: "";
  //   position: absolute;
  //   right: 0;
  //   bottom: 0;
  //   left: 0;
  //   z-index: -1;
  //   display: ${props => (props.isActionTaken ? 'none' : 'block')};
  //   height: 100%;
  //   background: inherit;
  //   transform: skewY(10deg);
  //   transform-origin: bottom left;
  // }
`;

export const Logo = styled.img`
  margin-top: 10px;
  max-height: 120px;
  padding-left: 0;

  @media ${small} {
    max-height: 40px;
    margin-top: 15px;
  }
`;

export const LogoWrapper = styled.div`
  //position: absolute;
  position: static;
  top: 10px;
  left: 10px;

  @media ${small} {
    top: 0;
    left: 0;
    margin-bottom: 10px;
  }

  @media ${desktop} {
    margin-bottom: 10px;
    width: 100%;
    max-width: ${maxDesktopWidth};
    //margin-left: auto;
    //margin-right: auto;
    padding: 0;
  }
`;
